import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  BarChart3, 
  PieChart, 
  Calculator,
  Info,
  AlertTriangle,
  CheckCircle,
  Activity
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const FundamentalAnalysis = ({ symbol, onClose, currentPrice }) => {
  const { t } = useLanguage();
  const [fundamentalData, setFundamentalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScoreInfo, setShowScoreInfo] = useState(false);

  useEffect(() => {
    const fetchFundamentalData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const API_BASE_URL = process.env.NODE_ENV === 'production' 
          ? '/api' 
          : 'http://localhost:3001/api';
        
        const response = await fetch(`${API_BASE_URL}/fundamental/${symbol}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            window.location.reload();
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to fetch fundamental data');
        }
        
        const data = await response.json();
        setFundamentalData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (symbol) {
      fetchFundamentalData();
    }
  }, [symbol]);

  const retryFetch = () => {
    window.location.reload(); // Simple retry by reloading
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    if (Math.abs(value) >= 1e12) return `${(value / 1e12).toFixed(1)}T`;
    if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return value.toFixed(decimals);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'neutral';
    const r = rating.toLowerCase();
    if (r.includes('strong buy') || r.includes('buy')) return 'positive';
    if (r.includes('sell')) return 'negative';
    return 'neutral';
  };

  const getHealthScore = (data) => {
    if (!data) return { score: 0, status: t('unknown') };
    
    let score = 50; // Start with neutral
    
    // P/E ratio scoring
    if (data.peRatio && data.peRatio > 0 && data.peRatio < 25) score += 10;
    else if (data.peRatio > 30) score -= 10;
    
    // Debt to equity scoring
    if (data.debtToEquity && data.debtToEquity < 0.5) score += 15;
    else if (data.debtToEquity > 1.5) score -= 15;
    
    // ROE scoring
    if (data.returnOnEquity && data.returnOnEquity > 15) score += 15;
    else if (data.returnOnEquity < 5) score -= 10;
    
    // Revenue growth scoring
    if (data.revenueGrowth && data.revenueGrowth > 10) score += 10;
    else if (data.revenueGrowth < -5) score -= 15;
    
    score = Math.max(0, Math.min(100, score));
    
    if (score >= 80) return { score, status: t('excellent'), color: 'positive' };
    if (score >= 60) return { score, status: t('good'), color: 'neutral' };
    if (score >= 40) return { score, status: t('fair'), color: 'warning' };
    return { score, status: t('poor'), color: 'negative' };
  };

  if (loading) {
    return createPortal(
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }} onClick={onClose}>
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', minWidth: '300px'
        }} onClick={(e) => e.stopPropagation()}>
          <Activity size={32} style={{ marginBottom: '1rem', animation: 'spin 1s linear infinite' }} />
          <p>Loading fundamental data for {symbol}...</p>
          <button onClick={onClose} style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: 'none', border: 'none', fontSize: '1.5rem',
            cursor: 'pointer'
          }}>×</button>
        </div>
      </div>,
      document.body
    );
  }

  if (error) {
    return createPortal(
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }} onClick={onClose}>
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', minWidth: '300px'
        }} onClick={(e) => e.stopPropagation()}>
          <AlertTriangle size={32} color="#ef4444" style={{ marginBottom: '1rem' }} />
          <p>Error loading fundamental data: {error}</p>
          <button onClick={retryFetch} style={{
            background: '#667eea', color: 'white', border: 'none',
            padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer',
            marginTop: '1rem'
          }}>
            Try Again
          </button>
          <button onClick={onClose} style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: 'none', border: 'none', fontSize: '1.5rem',
            cursor: 'pointer'
          }}>×</button>
        </div>
      </div>,
      document.body
    );
  }

  const healthScore = getHealthScore(fundamentalData);

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '1200px',
          width: '90vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '1.5rem 2rem',
          borderRadius: '12px 12px 0 0',
          position: 'relative'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, paddingRight: '3rem' }}>
            {t('fundamentalAnalysis')} - {symbol}
          </h2>
          <button 
            onClick={onClose} 
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div className="fundamental-content">
          {/* Health Score Overview */}
          <div className="health-score-section">
            <div className="health-score-card">
              <div className="health-score-header">
                <CheckCircle size={20} />
                <span>{t('financialHealthScore')}</span>
                <button 
                  className="score-info-btn"
                  onClick={() => setShowScoreInfo(!showScoreInfo)}
                  title={t('howScoreCalculated')}
                >
                  <Info size={16} />
                </button>
              </div>
              
              {showScoreInfo && (
                <div className="score-info-tooltip">
                  <h4>{t('scoreCalculation')}</h4>
                  <div className="score-criteria">
                    <div className="criteria-item">
                      <strong>P/E Ratio:</strong>
                      <span>+10 if &lt;25, -10 if &gt;30</span>
                    </div>
                    <div className="criteria-item">
                      <strong>Debt/Equity:</strong>
                      <span>+15 if &lt;0.5, -15 if &gt;1.5</span>
                    </div>
                    <div className="criteria-item">
                      <strong>ROE:</strong>
                      <span>+15 if &gt;15%, -10 if &lt;5%</span>
                    </div>
                    <div className="criteria-item">
                      <strong>Revenue Growth:</strong>
                      <span>+10 if &gt;10%, -15 if &lt;-5%</span>
                    </div>
                  </div>
                  <div className="score-ranges">
                    <div className="range excellent">80-100: {t('excellent')}</div>
                    <div className="range good">60-79: {t('good')}</div>
                    <div className="range fair">40-59: {t('fair')}</div>
                    <div className="range poor">0-39: {t('poor')}</div>
                  </div>
                </div>
              )}
              <div className={`health-score ${healthScore.color}`}>
                <span className="score-number">{healthScore.score}</span>
                <span className="score-status">{healthScore.status}</span>
              </div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="metrics-grid">
            {/* Valuation Metrics */}
            <div className="metric-section">
              <h3><DollarSign size={18} /> {t('valuation')}</h3>
              <div className="metric-row">
                <span>{t('marketCap')}</span>
                <span>{formatNumber(fundamentalData?.marketCap)}</span>
              </div>
              <div className="metric-row">
                <span>{t('peRatio')}</span>
                <span>{fundamentalData?.peRatio ? fundamentalData.peRatio.toFixed(2) : 'N/A'}</span>
              </div>
              <div className="metric-row">
                <span>{t('pbRatio')}</span>
                <span>{fundamentalData?.priceToBook ? fundamentalData.priceToBook.toFixed(2) : 'N/A'}</span>
              </div>
              <div className="metric-row">
                <span>{t('eps')}</span>
                <span>${fundamentalData?.eps ? fundamentalData.eps.toFixed(2) : 'N/A'}</span>
              </div>
            </div>

            {/* Financial Health */}
            <div className="metric-section">
              <h3><BarChart3 size={18} /> {t('financialHealth')}</h3>
              <div className="metric-row">
                <span>{t('revenue')}</span>
                <span>{formatNumber(fundamentalData?.revenue)}</span>
              </div>
              <div className="metric-row">
                <span>{t('revenueGrowth')}</span>
                <span className={fundamentalData?.revenueGrowth > 0 ? 'positive' : 'negative'}>
                  {formatPercent(fundamentalData?.revenueGrowth)}
                </span>
              </div>
              <div className="metric-row">
                <span>{t('profitMargin')}</span>
                <span>{formatPercent(fundamentalData?.profitMargin)}</span>
              </div>
              <div className="metric-row">
                <span>{t('debtToEquity')}</span>
                <span className={fundamentalData?.debtToEquity > 1 ? 'negative' : 'positive'}>
                  {fundamentalData?.debtToEquity ? fundamentalData.debtToEquity.toFixed(2) : 'N/A'}
                </span>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="metric-section">
              <h3><PieChart size={18} /> {t('performance')}</h3>
              <div className="metric-row">
                <span>{t('roe')}</span>
                <span className={fundamentalData?.returnOnEquity > 15 ? 'positive' : 'neutral'}>
                  {formatPercent(fundamentalData?.returnOnEquity)}
                </span>
              </div>
              <div className="metric-row">
                <span>{t('roa')}</span>
                <span>{formatPercent(fundamentalData?.returnOnAssets)}</span>
              </div>
              <div className="metric-row">
                <span>{t('beta')}</span>
                <span>{fundamentalData?.beta ? fundamentalData.beta.toFixed(2) : 'N/A'}</span>
              </div>
              <div className="metric-row">
                <span>{t('dividendYield')}</span>
                <span>{formatPercent(fundamentalData?.dividendYield)}</span>
              </div>
            </div>

            {/* Analyst Recommendations */}
            <div className="metric-section">
              <h3><Calculator size={18} /> {t('analystOpinion')}</h3>
              <div className="metric-row">
                <span>{t('recommendation')}</span>
                <span className={getRatingColor(fundamentalData?.analystRating)}>
                  {fundamentalData?.analystRating || 'N/A'}
                </span>
              </div>
              <div className="metric-row">
                <span>{t('targetPrice')}</span>
                <span>${fundamentalData?.targetPrice ? fundamentalData.targetPrice.toFixed(2) : 'N/A'}</span>
              </div>
              <div className="metric-row">
                <span>{t('priceRange52w')}</span>
                <span>
                  ${fundamentalData?.fiftyTwoWeekLow ? fundamentalData.fiftyTwoWeekLow.toFixed(2) : 'N/A'} - 
                  ${fundamentalData?.fiftyTwoWeekHigh ? fundamentalData.fiftyTwoWeekHigh.toFixed(2) : 'N/A'}
                </span>
              </div>
              <div className="metric-row">
                <span>{t('volumeAvg')}</span>
                <span>{formatNumber(fundamentalData?.avgVolume, 0)}</span>
              </div>
            </div>
          </div>

          {/* Summary Insights */}
          <div className="insights-section">
            <h3><Info size={18} /> {t('keyInsights')}</h3>
            <div className="insights-grid">
              {fundamentalData?.peRatio && fundamentalData.peRatio < 15 && (
                <div className="insight positive">
                  <CheckCircle size={16} />
                  <span>{t('attractiveValuation')}</span>
                </div>
              )}
              {fundamentalData?.debtToEquity && fundamentalData.debtToEquity < 0.5 && (
                <div className="insight positive">
                  <CheckCircle size={16} />
                  <span>{t('lowDebtLevels')}</span>
                </div>
              )}
              {fundamentalData?.returnOnEquity && fundamentalData.returnOnEquity > 15 && (
                <div className="insight positive">
                  <CheckCircle size={16} />
                  <span>{t('strongROE')}</span>
                </div>
              )}
              {fundamentalData?.revenueGrowth && fundamentalData.revenueGrowth > 10 && (
                <div className="insight positive">
                  <TrendingUp size={16} />
                  <span>{t('strongRevenueGrowth')}</span>
                </div>
              )}
              {fundamentalData?.revenueGrowth && fundamentalData.revenueGrowth < -5 && (
                <div className="insight negative">
                  <TrendingDown size={16} />
                  <span>{t('decliningRevenue')}</span>
                </div>
              )}
              {fundamentalData?.peRatio && fundamentalData.peRatio > 30 && (
                <div className="insight warning">
                  <AlertTriangle size={16} />
                  <span>{t('highValuationWarning')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default React.memo(FundamentalAnalysis);